import { context } from '@shared-services/context-service';
import React, { useContext, useEffect } from 'react';
import { useEvents } from '../../hooks/useEvents';
import { BookerStateContext } from '../../hooks/useRuntimeCalBookingState';
import {
    EventServerModel,
    ServiceProviderStage,
    WidgetStep,
} from '../../types';
import BookerStep from './BookerStep/BookerStep';
import SelectEventsStep from './SelectEventsStep/SelectEventStep';
import StaffMemberSelectionStep from './SelectStaffMemberStep';

interface CurrentStepComponentProps {
    events: EventServerModel[] | null;
    serviceProviderStage: ServiceProviderStage;
}
const STEP_COMPONENTS = {
    [WidgetStep.STAFF_MEMBER_SELECTION]: StaffMemberSelectionStep,
    [WidgetStep.EVENT_SELECTION]: SelectEventsStep,
    [WidgetStep.BOOKER]: BookerStep,
};

function CurrentStepComponent({
    events,
    serviceProviderStage,
}: CurrentStepComponentProps) {
    const [state, actions] = useContext(BookerStateContext);
    const { calEventsMap } = useEvents();
    const editorStep = context.isEditor ? state.widgetEditorStep : null;
    const isCollectiveEvent =
        state.eventSelection?.schedulingType?.toLowerCase() === 'collective';

    useEffect(() => {
        // while in editor, if the current step is staff member selection and there is more than one event, set the event selection
        // if staff member step is shown - choose the first event
        if (
            context.isEditor &&
            editorStep === WidgetStep.STAFF_MEMBER_SELECTION &&
            events &&
            events.length >= 1
        ) {
            const calEventData = Array.from(calEventsMap.values()).find(
                (event) => event.hosts.length > 1
            );
            if (calEventData) {
                actions.setEventSelection(calEventData);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorStep, events]);

    useEffect(() => {
        if (calEventsMap.size === 1 && !state.eventSelection) {
            const singleEventData = Array.from(calEventsMap.values())[0];
            actions.setEventSelection(singleEventData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calEventsMap]);

    const isMultipleEvents = events && events.length > 1;
    const currentEventSlug = state.eventSlug;
    const isMultipleEventsNoEventSlug = isMultipleEvents && !currentEventSlug;
    const isStaffMemberSelection =
        state.eventSelection && state.eventSelection.hosts.length > 1;

    const isMultiSeats = state.eventSelection?.seats?.disabled === false;

    const isStaffMemberSelectionStage =
        serviceProviderStage.isStageShown &&
        isStaffMemberSelection &&
        !state.selectedHostId &&
        !isMultiSeats &&
        !isCollectiveEvent;

    const currentStep = isMultipleEventsNoEventSlug
        ? WidgetStep.EVENT_SELECTION
        : isStaffMemberSelectionStage
        ? WidgetStep.STAFF_MEMBER_SELECTION
        : WidgetStep.BOOKER;

    const StepComponent = STEP_COMPONENTS[editorStep || currentStep];

    return <StepComponent />;
}

export default CurrentStepComponent;
